import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { EntityGalleryTileResponseDto } from '../dto/response/entity-gallery-tile-response.dto';
import entityGalleryTileService from '../services/entity-gallery-tile.service';
import { EntityGalleryTileSaveRequestDto } from '../dto/request/entity-gallery-tile-save-request.dto';
import { EntityGalleryTileUpdateRequestDto } from '../dto/request/entity-gallery-tile-update-request.dto';
import { showError, showSuccess } from '../../../../utils/Notifications';
import { AxiosError } from 'axios';

const initialState = {
  status: 'idle',
  entityGalleryTiles: [] as EntityGalleryTileResponseDto[] | null,
  entityGalleryTile: {} as EntityGalleryTileResponseDto | null,
  error: null as string | null,
};

export const fetchEntityGalleryTileById = createAsyncThunk(
  'entityGalleryTiles/fetchEntityGalleryTileById',
  async (id: string) => {
    return await entityGalleryTileService.fetchEntityGalleryTileById(id);
  }
);

export const saveEntityGalleryTile = createAsyncThunk(
  'entityGalleryTiles/saveEntityGalleryTile',
  async (saveRequest: EntityGalleryTileSaveRequestDto, { rejectWithValue }) => {
    try {
      return await entityGalleryTileService.saveEntityGalleryTile(saveRequest);
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

export const updateEntityGalleryTile = createAsyncThunk(
  'entityGalleryTiles/updateEntityGalleryTile',
  async (updateRequest: EntityGalleryTileUpdateRequestDto) => {
    return await entityGalleryTileService.updateEntityGalleryTile(
      updateRequest
    );
  }
);

export const deleteEntityGalleryTile = createAsyncThunk(
  'entityGalleryTiles/deleteEntityGalleryTile',
  async (
    params: { existingId: string; compactId: string | null },
    { rejectWithValue }
  ) => {
    try {
      return await entityGalleryTileService.deleteEntityGalleryTile(
        params.existingId,
        params.existingId
      );
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

const entityGalleryTileSlice = createSlice({
  name: 'entityGalleryTileSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEntityGalleryTileById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchEntityGalleryTileById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.entityGalleryTile = action.payload;
      })
      .addCase(fetchEntityGalleryTileById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(saveEntityGalleryTile.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveEntityGalleryTile.fulfilled, (state, action) => {
        state.status = 'succeeded';
        showSuccess('Gallery tile added successfully.');
        state.entityGalleryTiles?.push(action.payload);
      })
      .addCase(saveEntityGalleryTile.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Saving gallery tile failed. ${action.payload as string}`);
      })
      .addCase(updateEntityGalleryTile.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateEntityGalleryTile.fulfilled, (state, action) => {
        state.status = 'succeeded';
        showSuccess('Gallery tile saved successfully.');
        if (state.entityGalleryTiles) {
          const entityGalleryTileIndex = state.entityGalleryTiles.findIndex(
            (entityGalleryTile) => entityGalleryTile.id === action.payload.id
          );
          if (entityGalleryTileIndex !== -1) {
            state.entityGalleryTiles[entityGalleryTileIndex] = action.payload;
          }
        }
      })
      .addCase(updateEntityGalleryTile.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(deleteEntityGalleryTile.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteEntityGalleryTile.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('Gallery tile deleted successfully.');
      })
      .addCase(deleteEntityGalleryTile.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Deleting gallery tile failed. ${action.payload as string}`);
      });
  },
});

export default entityGalleryTileSlice.reducer;
